<!-- The choice itself is the card: one of the two ladders is always selected, and every PO submits
     one. There is no third "off" state -- the first preset renders exactly what the template
     renders with no encoding_ladder key at all, so an explicit selection costs nothing and is
     honest about what the job will do. -->
<div class="row">
  <div class="col-12">
    <fieldset>
        <legend class="sr-only it-ladder-preset-legend">Encoding ladder</legend>
        <div class="cl-spacing-tiny">
          @for (p of presets; track p.id) {
            <div [class.cl-spacing-small]="!$first">
              <app-dl-radio
                [radioId]="'preset_' + p.id"
                [value]="p.id"
                [label]="p.label"
                [description]="p.description"
                [disabled]="form.disabled"
                [ngModel]="presetId"
                (ngModelChange)="applyPreset($event)"
                [ngModelOptions]="{standalone: true}"
                name="ladder_preset"></app-dl-radio>
            </div>
          }
        </div>

        <p class="alert cl-alert-info" role="alert">
          Presets are starting points; expand the sections below to adjust them. Choosing a preset again replaces your changes
        </p>

          <div [formGroup]="form">

    <div class="row cl-spacing-micro">
      <div class="col-12">
        <details class="card cl-tight it-ladder-card it-ladder-source">
          <summary class="card-header d-flex align-items-center">
            <strong>Source</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-source-summary">{{ sourceSummary }}</span>
            @if (sourceChanged) {
              <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-source-changed">Changed</span>
            }
          </summary>
          <div class="card-body">
            <app-dl-checkbox
              checkboxId="audio_only"
              label="Audio only — no video"
              description="Drops every video rendition — no codec groups, just the audio set below packaged straight to the outputs picked here. Only for a source with no video track: the template refuses a video ladder there, and refuses this ladder on a source that does have video."
              [disabled]="form.disabled"
              [ngModel]="audioOnly"
              (ngModelChange)="setAudioOnly($event)"
              [ngModelOptions]="{standalone: true}"></app-dl-checkbox>

            @if (!audioOnly) {
              <div class="cl-spacing-small">
                <app-dl-checkbox
                  checkboxId="exceed_input_resolution"
                  label="Allow upscaling"
                  description="Upscale beyond the source resolution. Off drops any rendition wider than the source rather than upscaling to it."
                  formControlName="exceed_input_resolution"></app-dl-checkbox>
              </div>
            }
          </div>
        </details>
      </div>
    </div>

    <!-- Both settings are ladder-level, not per codec group: the packager takes a single segment
         duration for the whole job, so a per-group GOP could not be honoured, and a segment that is
         not a whole number of GOPs would not start on a keyframe. -->
    <div class="row cl-spacing-micro">
      <div class="col-12">
        <details class="card cl-tight it-ladder-card it-ladder-gop">
          <summary class="card-header d-flex align-items-center">
            <strong>GOP</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-gop-summary">{{ gopSummary }}</span>
            @if (gopChanged) {
              <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-gop-changed">Changed</span>
            }
            @if (gopInvalid) {
              <span class="cl-badge cl-badge-sm cl-color-brick ms-2 it-ladder-gop-invalid">Invalid</span>
            }
          </summary>
          <div class="card-body">
            <p>GOP (Group of Pictures) is the span between two keyframes. Players can only start or switch quality at a keyframe.</p>
            <div class="row">
              <div class="col-12 col-md-6">
                <label class="form-label" for="gop_in_seconds">Seconds</label>
                <input
                  [class.invalid]="form.get('gop_in_seconds').errors && form.get('gop_in_seconds').touched"
                  class="form-control" formControlName="gop_in_seconds" id="gop_in_seconds" min="1" step="1" type="number">
                <!-- Only shown once the field is touched, and directly after the input: that is what the library keys on. -->
                @if (form.get('gop_in_seconds').errors?.['required']) {
                  <p class="invalid-feedback">Enter the GOP length in seconds.</p>
                } @else if (form.get('gop_in_seconds').errors?.['min']) {
                  <p class="invalid-feedback">Must be at least 1.</p>
                }
                <p class="valid-feedback">Closed-GOP length. Keyframes align within a group, and every group cuts at the same frames.</p>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label" for="gops_per_segment">GOPs per segment</label>
                <input
                  [class.invalid]="form.get('gops_per_segment').errors && form.get('gops_per_segment').touched"
                  class="form-control" formControlName="gops_per_segment" id="gops_per_segment" min="1" step="1" type="number">
                <!-- Only shown once the field is touched, and directly after the input: that is what the library keys on. -->
                @if (form.get('gops_per_segment').errors?.['required']) {
                  <p class="invalid-feedback">Enter how many GOPs make a segment.</p>
                } @else if (form.get('gops_per_segment').errors?.['min']) {
                  <p class="invalid-feedback">Must be at least 1.</p>
                }
                <p class="valid-feedback">A segment has to start on a keyframe, so this is a whole number of GOPs.</p>
              </div>
            </div>
            @if (segmentDuration() !== null) {
              <p class="cl-spacing-small it-ladder-segment-duration">Segment duration: {{ segmentDuration() }} s — what the packager is handed.</p>
            }
          </div>
        </details>
      </div>
    </div>

    <!-- Switching groups: a player picks one and switches only within its renditions, which is why
         keyframe alignment and codec settings are per group and never across them.

         Every list below tracks the *control instance*, not $index. With $index, removing a row
         other than the last one made the survivors render the wrong values: Angular reused the
         row's DOM, and because `[formGroupName]="i"` kept the same value the directive never
         re-resolved, so the surviving row went on showing the control that had just been removed --
         which reads as "it deleted the last one instead".

         Identity tracking makes Angular log NG0956 when a preset is applied, because _load really
         does replace every control. That warning is the honest cost of the fix and fires on nothing
         else: not on edits, not on removals. Do not quiet it by going back to $index. -->
    @if (!audioOnly) {
    <div class="row cl-spacing-micro">
      <div class="col-12">
        <details class="card cl-tight it-ladder-card it-ladder-video">
          <summary class="card-header d-flex align-items-center">
            <strong>Video codecs &amp; renditions</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-video-summary">{{ videoSummary }}</span>
            @if (videoChanged) {
              <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-video-changed">Changed</span>
            }
            @if (videoInvalid) {
              <span class="cl-badge cl-badge-sm cl-color-brick ms-2 it-ladder-video-invalid">Invalid</span>
            }
          </summary>
          <div class="card-body">
            <div class="row">
              <div class="col-12 col-md-9">
                <p>One group per codec; each group holds its own renditions (the quality rungs).</p>
              </div>
              <div class="col-12 col-md-3">
                <div class="cl-flex-center-center">
                  @if (canAddGroup()) {
                    <button (click)="addGroup()" class="btn btn-sm cl-color-secondary it-ladder-add-group" type="button">Add codec group</button>
                  }
                </div>
              </div>
            </div>
    <div formArrayName="groups">
      @for (group of groups.controls; track group; let gi = $index) {
        <!-- A card per group, so how many there are reads at a glance. A group, not a fieldset: the
             title is the header's, and the name the group is announced by. -->
        <div [formGroupName]="gi" [attr.aria-labelledby]="'codec_group_title_' + gi"
             class="card cl-tight cl-color-haze cl-spacing-small it-ladder-group" role="group">
          <div class="card-header d-flex align-items-center">
            <strong [id]="'codec_group_title_' + gi" class="it-ladder-group-title">Codec group {{ gi + 1 }}</strong>
            @if (groups.length > 1) {
              <button (click)="removeGroup(gi)" [attr.aria-label]="'Delete codec group ' + (gi + 1)"
                      class="cl-btn-quickaction ms-auto it-ladder-group-delete" type="button">Delete</button>
            }
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-12 mt-0">
                <label class="form-label" for="codec_{{gi}}">Codec</label>
                <select class="form-select" formControlName="codec" id="codec_{{gi}}">
                  @for (c of availableCodecs(gi); track c) {
                    <option [ngValue]="c">{{ codecs[c].label }} ({{ codecs[c].encoder }})</option>
                  }
                </select>
                <p class="valid-feedback">The video encoder for this group. A player switches quality only within one group, so each codec gets its own.</p>
              </div>
            </div>

            <div class="row cl-spacing-small">
              <div class="col-12 col-md-4">
                <label class="form-label" for="profile_{{gi}}">Profile</label>
                <select class="form-select" formControlName="profile" id="profile_{{gi}}">
                  <option [ngValue]="null">Codec default</option>
                  @for (p of profilesFor(group); track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
                <p class="valid-feedback">Limits which encoder features the stream may use, and so which devices can decode it. Codec default lets the encoder choose.</p>
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label" for="preset_{{gi}}">Preset</label>
                <select class="form-select" formControlName="preset" id="preset_{{gi}}">
                  <option [ngValue]="null">Codec default</option>
                  @for (p of presetsFor(group); track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
                <p class="valid-feedback">Trades encoding time for compression: a slower preset gives better quality at the same bitrate, but takes longer.</p>
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label" for="pixfmt_{{gi}}">Pixel format</label>
                <select class="form-select" formControlName="pixfmt" id="pixfmt_{{gi}}">
                  @for (p of pixelFormats; track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
                <p class="valid-feedback">Colour sampling and bit depth. yuv420p is 8-bit and plays everywhere; the 10-bit formats need a profile deep enough to carry them.</p>
              </div>
            </div>
            @if (group.errors?.profileTooShallow) {
              <p class="invalid-feedback d-block it-ladder-group-profile-too-shallow">
                {{ group.errors.profileTooShallow }} — the encoder refuses to open on that pair rather
                than falling back, so pick a deeper profile or an 8-bit pixel format.
              </p>
            }

            <div [attr.aria-labelledby]="'outputs_label_' + gi" class="cl-spacing-small" role="group">
              <label [id]="'outputs_label_' + gi" class="form-label">Outputs</label>
              <app-dl-checkbox
                [checkboxId]="'out_dash_' + gi"
                [invalid]="!!group.errors?.noOutputs"
                description="Packages this group for MPEG-DASH and lists it in the DASH manifest."
                formControlName="out_dash"
                label="DASH"></app-dl-checkbox>
              <div class="cl-spacing-small">
                <app-dl-checkbox
                  [checkboxId]="'out_hls_' + gi"
                  [invalid]="!!group.errors?.noOutputs"
                  description="Packages this group for HTTP Live Streaming and lists it in the HLS manifest. Needed for Apple devices."
                  formControlName="out_hls"
                  label="HLS"></app-dl-checkbox>
              </div>
              @if (group.errors?.noOutputs) {
                <p class="invalid-feedback d-block it-ladder-group-no-outputs">Pick at least one output — DASH, HLS, or both.</p>
              }
            </div>

            <div [attr.aria-labelledby]="'rate_control_label_' + gi" class="cl-spacing-small" role="group">
              <label [id]="'rate_control_label_' + gi" class="form-label">Rate control</label>
              <app-dl-checkbox
                [checkboxId]="'two_pass_' + gi"
                description="Encodes each rendition in two passes to land on its average bitrate, which keeps quality steadier at a given size. Slower than a single constant-quality (CRF) pass."
                formControlName="two_pass"
                label="Two-pass VBR">
                @if (!supportsTwoPass(group)) {
                  <p class="valid-feedback">
                    SVT-AV1 cannot target an average under a peak cap, so AV1 stays on CRF.
                  </p>
                }
                @if (group.errors?.twoPassUnsupported) {
                  <p class="invalid-feedback d-block it-ladder-group-two-pass-unsupported">
                    {{ codecs[group.errors.twoPassUnsupported].label }} cannot do two-pass VBR. Leave it off.
                  </p>
                }
              </app-dl-checkbox>
              <div>
                <label class="form-label" for="crf_{{gi}}">CRF</label>
                <!-- Disabled, not readonly, when two-pass is on: the value is not sent at all, and a
                     readonly input paints exactly like a live one. -->
                <input
                  [class.invalid]="group.get('crf').errors && group.get('crf').touched"
                  class="form-control"
                  formControlName="crf"
                  id="crf_{{gi}}"
                  [max]="crfMax(group)"
                  min="0"
                  step="0.1"
                  type="number">
                @if (group.get('crf').errors?.max) {
                  <p class="invalid-feedback d-block it-ladder-group-crf-error">
                    {{ codecs[group.get('codec').value].label }} accepts CRF 0–{{ crfMax(group) }}; the encoder
                    refuses to open above that.
                  </p>
                } @else if (group.get('crf').errors?.required) {
                  <p class="invalid-feedback d-block it-ladder-group-crf-error">
                    A capped rendition without its own CRF or average bitrate encodes at the group's CRF —
                    state one here, or give every rendition its own target.
                  </p>
                } @else {
                  <p class="valid-feedback">
                    Constant-quality target for single-pass encodes, 0–{{ crfMax(group) }} for this codec: lower means higher
                    quality and a larger file. Not used while Two-pass VBR is on; a rendition can set its own.
                  </p>
                }
              </div>
            </div>

            @if (supportsEncoderParams(group) || group.get('encoder_params').value) {
              <div class="row cl-spacing-small">
                <div class="col-12">
                  <label class="form-label" for="encoder_params_{{gi}}">Encoder parameters</label>
                  <input
                    [class.invalid]="group.get('encoder_params').errors"
                    [placeholder]="group.get('codec').value === 'av1' ? 'enable-variance-boost=1:tf-strength=1' : 'sao=0:psy-rd=1'"
                    class="form-control"
                    formControlName="encoder_params"
                    id="encoder_params_{{gi}}"
                    type="text">
                  @if (group.get('encoder_params').errors?.encoderParamsSyntax) {
                    <p class="invalid-feedback d-block">Write name=value pairs separated by colons.</p>
                  } @else if (group.get('encoder_params').errors?.encoderParamsUnsupported) {
                    <p class="invalid-feedback d-block">This codec does not support encoder parameters; clear this field.</p>
                  } @else if (group.get('encoder_params').errors?.encoderParamsReserved) {
                    <p class="invalid-feedback d-block">
                      {{ group.get('encoder_params').errors.encoderParamsReserved }} — set by the template
                      itself (GOP, colour, threads, preset, profile, level, passes or rate control); use the ladder's
                      own fields.
                    </p>
                  } @else {
                    <p class="valid-feedback">
                      Appended to {{ group.get('codec').value === 'av1' ? '-svtav1-params' : '-x265-params' }}.
                    </p>
                  }
                </div>
              </div>
            }


            <!-- Renditions: width also decides which DRM tier's key a rendition is encrypted with, and
                 max_bitrate keys the output filename. A blank max_bitrate encodes the rung uncapped,
                 named after its own avg_bitrate or CRF instead. -->
            <div class="row cl-spacing-small">
              <div class="col-12">
                <label class="form-label">Renditions</label>
              </div>
            </div>
            <!-- Column headings are visual only: each row's inputs carry their own aria-label, since a
                 heading above a repeated column cannot be a <label for> every row. -->
            <div aria-hidden="true" class="row g-2">
              <div class="col-2"><label class="form-label">Width</label></div>
              <div class="col-2"><label class="form-label">Avg bitrate</label></div>
              <div class="col-2"><label class="form-label">Max bitrate</label></div>
              <div class="col-1"><label class="form-label">CRF</label></div>
              <div class="col-2"><label class="form-label">Min bitrate</label></div>
              <div class="col-2"><label class="form-label">Bufsize</label></div>
              <div class="col-1"></div>
            </div>
            <div formArrayName="renditions">
              @for (rendition of renditionsOf(group).controls; track rendition; let ri = $index) {
                <div [formGroupName]="ri" class="row g-2 align-items-end">
                  <div class="col-2">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' width'" [class.invalid]="rendition.get('width').errors" class="form-control" formControlName="width" min="1" step="1" type="number">
                  </div>
                  <div class="col-2">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' average bitrate'" [class.invalid]="rendition.get('avg_bitrate').errors || rendition.errors?.uncappedWithoutTarget || rendition.errors?.crfAndAvg" class="form-control" formControlName="avg_bitrate" placeholder="—" type="text">
                  </div>
                  <div class="col-2">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' max bitrate'" [class.invalid]="rendition.get('max_bitrate').errors || rendition.errors?.uncappedWithoutTarget || rendition.errors?.vbvWithoutCap" class="form-control" formControlName="max_bitrate" placeholder="uncapped" type="text">
                  </div>
                  <div class="col-1">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' CRF'" [class.invalid]="rendition.get('crf').errors || rendition.errors?.crfAndAvg || rendition.errors?.uncappedWithoutTarget" class="form-control" formControlName="crf" [max]="crfMax(group)" min="0" placeholder="—" step="0.1" type="number">
                  </div>
                  <div class="col-2">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' min bitrate'" [class.invalid]="rendition.get('min_bitrate').errors || (rendition.errors?.vbvWithoutCap && rendition.get('min_bitrate').value)" class="form-control" formControlName="min_bitrate" placeholder="—" type="text">
                  </div>
                  <div class="col-2">
                    <input [attr.aria-label]="codecs[group.get('codec').value].label + ' rendition ' + (ri + 1) + ' bufsize'" [class.invalid]="rendition.get('bufsize').errors || (rendition.errors?.vbvWithoutCap && rendition.get('bufsize').value)" class="form-control" formControlName="bufsize" placeholder="—" type="text">
                  </div>
                  <div class="col-1 text-end">
                    <button
                      (click)="removeRendition(group, ri)"
                      [disabled]="renditionsOf(group).length < 2"
                      aria-label="Remove rendition"
                      class="cl-btn-quickaction"
                      type="button"><i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                @if (rendition.errors?.vbvWithoutCap) {
                  <p class="invalid-feedback d-block">
                    Bufsize and min bitrate only act under a max bitrate — clear them, or set a cap.
                  </p>
                }
                @if (rendition.errors?.uncappedWithoutTarget) {
                  <p class="invalid-feedback d-block">
                    @if (codecs[group.get('codec').value].twoPass) {
                      No max bitrate means an uncapped rung — give it its own average bitrate or CRF.
                    } @else {
                      No max bitrate means an uncapped rung — give it its own CRF.
                    }
                    The group's CRF covers capped rungs only: an uncapped rung's own target names its file.
                  </p>
                }
                @if (rendition.get('avg_bitrate').errors?.required) {
                  <p class="invalid-feedback d-block">
                    Two-pass needs an average bitrate on every rendition — none may be left blank
                    (or give the rung its own CRF to keep it single-pass).
                  </p>
                }
                @if (rendition.get('avg_bitrate').errors?.avgUnsupported) {
                  <p class="invalid-feedback d-block">
                    {{ codecs[group.get('codec').value].label }} has no average-bitrate mode — target a CRF
                    instead; add a max bitrate only when a cap is desired.
                  </p>
                }
                @if (rendition.get('crf').errors?.max) {
                  <p class="invalid-feedback d-block">
                    {{ codecs[group.get('codec').value].label }} accepts CRF 0–{{ crfMax(group) }}; the
                    encoder refuses to open above that.
                  </p>
                }
                @if (rendition.errors?.crfAndAvg) {
                  <p class="invalid-feedback d-block">A rung targets either a CRF or an average bitrate, not both.</p>
                }
              }
            </div>
            @if (renditionsOf(group).errors?.noRenditions) {
              <p class="invalid-feedback d-block">A group needs at least one rendition.</p>
            }
            @if (group.get('two_pass').value) {
              <p class="valid-feedback">
                Two-pass targets each rendition's average bitrate (under its max, if it has one). A rung
                with its own CRF instead stays a single constant-quality pass.
              </p>
            }

            <div class="cl-spacing-box">
              <button (click)="addRendition(group)" class="btn btn-sm cl-color-secondary" type="button">Add rendition</button>
            </div>
          </div>
        </div>
      }
    </div>

    @if (groups.errors?.duplicateCodec) {
      <p class="invalid-feedback d-block">
        Two groups use codec "{{ groups.errors.duplicateCodec }}" — one group per codec.
      </p>
    }
          </div>
        </details>
      </div>
    </div>
    } @else {
      <!-- No group left to carry outputs, so an audio-only ladder states them once, here. -->
      <div class="row cl-spacing-micro">
        <div class="col-12">
          <details class="card cl-tight it-ladder-card it-ladder-outputs">
            <summary class="card-header d-flex align-items-center">
              <strong>Outputs</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-outputs-summary">{{ outputsSummary }}</span>
              @if (outputsChanged) {
                <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-outputs-changed">Changed</span>
              }
              @if (outputsInvalid) {
                <span class="cl-badge cl-badge-sm cl-color-brick ms-2 it-ladder-outputs-invalid">Invalid</span>
              }
            </summary>
            <div class="card-body">
              <app-dl-checkbox
                checkboxId="ladder_out_dash"
                [invalid]="!!groups.errors?.noAudioOutputs"
                description="Packages the audio for MPEG-DASH and lists it in the DASH manifest."
                formControlName="ladder_out_dash"
                label="DASH"></app-dl-checkbox>
              <div class="cl-spacing-small">
                <app-dl-checkbox
                  checkboxId="ladder_out_hls"
                  [invalid]="!!groups.errors?.noAudioOutputs"
                  description="Packages the audio for HTTP Live Streaming and lists it in the HLS manifest. Needed for Apple devices."
                  formControlName="ladder_out_hls"
                  label="HLS"></app-dl-checkbox>
              </div>
              @if (groups.errors?.noAudioOutputs) {
                <p class="invalid-feedback d-block it-ladder-outputs-error">Pick at least one output — DASH, HLS, or both.</p>
              }
            </div>
          </details>
        </div>
      </div>
    }

    <!-- Shared across every group: one audio set is referenced by all of them, and the DRM split
         is by width regardless of codec. -->
    <div class="row cl-spacing-micro">
      <div class="col-12">
        <details class="card cl-tight it-ladder-card it-ladder-audio">
          <summary class="card-header d-flex align-items-center">
            <strong>Audio</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-audio-summary">{{ audioSummary }}</span>
            @if (audioChanged) {
              <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-audio-changed">Changed</span>
            }
            @if (audioInvalid) {
              <span class="cl-badge cl-badge-sm cl-color-brick ms-2 it-ladder-audio-invalid">Invalid</span>
            }
          </summary>
          <div class="card-body">
    <div class="row">
      <div class="col-12 mt-0">
        <label class="form-label" for="audio_mode">Audio mode</label>
        <select (change)="setAudioMode($any($event.target).value)" class="form-select" id="audio_mode">
          @for (m of audioStartModes; track m.id) {
            <option [value]="m.id" [selected]="m.id === form.get('audio_mode').value">{{ m.label }}</option>
          }
          <option [value]="form.get('audio_mode').value" [selected]="isCustomAudio()" [disabled]="!isCustomAudio()">
            Custom
          </option>
        </select>
        <p class="valid-feedback">{{ audioModeDescription() }}</p>
      </div>
    </div>

    <!-- One table for all three modes. A row is one output rendition -- one channel layout in one codec
         -- which is already the shape of an audio_renditions entry and what an audio_layouts entry
         collapses to per codec. Columns a mode does not write are disabled rather than removed, so
         the table keeps one shape and it stays visible which knobs exist. -->
    <div class="row g-2">
      <div class="col-2"><label class="form-label">Channels</label></div>
      <div class="col-2"><label class="form-label">Codec</label></div>
      <div class="col-2"><label class="form-label">Bitrate</label></div>
      <div class="col-2"><label class="form-label">Sample rate</label></div>
      <div class="col-1"><label class="form-label">Menu name</label></div>
      <div class="col-2"><label class="form-label">Layout policy</label></div>
      <div class="col-1"></div>
    </div>
    <div formArrayName="audio_rows">
      @for (row of audioRows.controls; track row; let ri = $index) {
        <div [formGroupName]="ri" class="row g-2 align-items-end">
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('channels') ? null : ''"
              [class.invalid]="row.get('channels').errors"
              [placeholder]="audioColumnApplies('channels') ? 'source' : 'source'"
              class="form-control" formControlName="channels" min="1" step="1" type="number">
          </div>
          <div class="col-2">
            <select
              [attr.disabled]="audioColumnApplies('codec') ? null : ''"
              class="form-select" formControlName="codec">
              @for (c of audioCodecsFor(row); track c) {
                <option [ngValue]="c">{{ codecLabels([c]) }}</option>
              }
            </select>
          </div>
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('bitrate') ? null : ''"
              [class.invalid]="row.get('bitrate').errors"
              class="form-control" formControlName="bitrate" placeholder="source" type="text">
          </div>
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('samplerate') ? null : ''"
              class="form-control" formControlName="samplerate" placeholder="source" step="1" type="number">
          </div>
          <div class="col-1">
            <input
              [attr.disabled]="audioColumnApplies('display') ? null : ''"
              class="form-control" formControlName="display" placeholder="none" type="text">
          </div>
          <div class="col-2">
            <div class="form-check form-check-inline">
              <input
                [attr.disabled]="audioColumnApplies('required') ? null : ''"
                class="form-check-input" formControlName="required" id="required_{{ri}}" type="checkbox">
              <label class="form-check-label" for="required_{{ri}}">Required</label>
            </div>
            <div class="form-check form-check-inline">
              <input
                [attr.disabled]="audioColumnApplies('allow_downmix') ? null : ''"
                class="form-check-input" formControlName="allow_downmix" id="downmix_{{ri}}" type="checkbox">
              <label class="form-check-label" for="downmix_{{ri}}">Accept downmix</label>
            </div>
          </div>
          <div class="col-1 text-end">
            @if (audioRows.length > 1) {
              <button
                (click)="removeAudioRow(ri)"
                [disabled]="audioRows.length < 2"
                aria-label="Remove audio rendition"
                class="cl-btn-quickaction"
                type="button"><i class="fas fa-times" aria-hidden="true"></i>
              </button>
            }
          </div>
        </div>
      }
    </div>

    @if (audioRows.errors?.duplicatePair) {
      <p class="invalid-feedback d-block">
        Two rows deliver {{ audioRows.errors.duplicatePair }} — that pairing is one rendition, so it would
        be encoded twice into the same file.
      </p>
    }
    @if (audioRows.errors?.inconsistentLayout) {
      <p class="invalid-feedback d-block">
        The rows for {{ layoutName(audioRows.errors.inconsistentLayout) }} disagree on bitrate, menu name
        or delivery — they are one layout in several codecs, so those have to match.
      </p>
    }
    @if (audioRows.errors?.stereoOnlyCodec) {
      <p class="invalid-feedback d-block">
        {{ audioRows.errors.stereoOnlyCodec }} carries its second channel parametrically and is only
        defined for stereo — set that row to 2 channels, or pick another codec.
      </p>
    }
    @if (audioRows.errors?.noRequiredLayout) {
      <p class="invalid-feedback d-block">
        Mark one layout <b>Required</b>, or a source in a layout that is not listed ships with no audio
        the player is guaranteed to have.
      </p>
    }
    @if (audioRows.errors?.duplicateRendition) {
      <p class="invalid-feedback d-block">
        Two renditions agree on codec, channels and bitrate ("{{ audioRows.errors.duplicateRendition }}") —
        together those name the output file, so one would overwrite the other.
      </p>
    }
    @if (audioRows.errors?.noRows) {
      <p class="invalid-feedback d-block">Add at least one rendition.</p>
    }

    <p class="valid-feedback">
      @if (form.get('audio_mode').value === 'custom_renditions') {
        Every track gets one output per row, whatever layout it arrived in. Leave a field empty to keep
        the source value.
      } @else {
        One row per layout and codec. <b>Required</b> means the layout is delivered even when no source
        track is in it; <b>Accept downmix</b> allows it to be derived from a wider source. A required
        layout that cannot accept a downmix is only delivered when a source track already has it.
        Nothing is ever up-mixed.
        @if (audioRowsAreDefault()) {
          These are the template's own defaults, so the job stays on whatever it derives.
        } @else {
          Edited — the job carries this policy explicitly.
        }
      }
    </p>

    <!-- Both in one spacing box: cl-spacing-box spaces the buttons next to each other. -->
    <div class="cl-spacing-box">
      <button (click)="addAudioRow()" class="btn btn-sm cl-color-secondary" type="button">Add rendition</button>
      @if (!audioRowsAreDefault()) {
        <button (click)="resetAudioRows()" class="btn btn-sm cl-color-secondary" type="button">
          Reset to defaults
        </button>
      }
    </div>
          </div>
        </details>
      </div>
    </div>

    <div class="row cl-spacing-micro">
      <div class="col-12">
        <details class="card cl-tight it-ladder-card it-ladder-drm">
          <summary class="card-header d-flex align-items-center">
            <strong>DRM key tiers</strong>&nbsp;·&nbsp;<span class="ladder-card-overview it-ladder-drm-summary">{{ drmSummary }}</span>
            @if (drmChanged) {
              <span class="cl-badge cl-badge-sm cl-color-sea ms-2 it-ladder-drm-changed">Changed</span>
            }
            @if (drmInvalid) {
              <span class="cl-badge cl-badge-sm cl-color-brick ms-2 it-ladder-drm-invalid">Invalid</span>
            }
          </summary>
          <div class="card-body">
    <div class="row">
      <div class="col-12">
        @if (audioOnly) {
          <p>
            Audio is keyed on its own AUDIO tier, requested for every job — there is no video
            rendition left to split further by width.
          </p>
        } @else {
          <p>
            Which DRM key a rendition is encrypted with, by width. Effectively global: the same content is
            reused across deliveries, so two jobs mapping a width onto different tiers would request
            different keys for the same rendition. Change this only if every job for the content agrees.
          </p>
        }
      </div>
    </div>
    @if (!audioOnly) {
    <div class="row g-2">
      <div class="col-5"><label class="form-label">Stream type</label></div>
      <div class="col-5"><label class="form-label">Max width (inclusive)</label></div>
      <div class="col-2"></div>
    </div>
    <!-- Shown, not offered: audio is keyed separately from video and the key is requested for
         every job, so there is nothing to decide. It is in the submitted ladder all the same --
         the stored PO is the only record of the key set a job ran with. -->
    <div class="row g-2 align-items-end">
      <div class="col-5">
        <input class="form-control" disabled type="text" value="AUDIO">
      </div>
      <div class="col-5">
        <input class="form-control" disabled placeholder="by stream type, not width" type="text">
      </div>
      <div class="col-2"></div>
    </div>
    <div formArrayName="drm_streamtypes">
      @for (tier of drmTiers.controls; track tier; let ti = $index) {
        <div [formGroupName]="ti" class="row g-2 align-items-end">
          <div class="col-5">
            <select class="form-select" formControlName="streamtype">
              @for (t of streamTypes; track t) {
                <option [ngValue]="t">{{ t }}</option>
              }
            </select>
          </div>
          <div class="col-5">
            @if (ti === drmTiers.length - 1) {
              <input class="form-control" disabled placeholder="unbounded" type="text">
            } @else {
              <input class="form-control" formControlName="max_width" min="1" step="1" type="number">
            }
          </div>
          <div class="col-2">
            <button
              (click)="removeDrmTier(ti)"
              [disabled]="drmTiers.length < 2"
              aria-label="Remove DRM tier"
              class="cl-btn-quickaction"
              type="button"><i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      }
    </div>
    @if (drmTiers.errors?.noTiers) {
      <p class="invalid-feedback d-block">A video ladder needs at least one width tier besides AUDIO.</p>
    }
    @if (drmTiers.errors?.tierOrder || drmTiers.errors?.duplicateTier) {
      <p class="invalid-feedback d-block">
        Declare tiers ascending and once each, in the order {{ streamTypes.join(', ') }}.
      </p>
    }
    @if (drmTiers.errors?.tierAscending || drmTiers.errors?.tierUnbounded) {
      <p class="invalid-feedback d-block">Every bounded tier needs a max width, and they must strictly ascend.</p>
    }
    @if (drmTiers.length < streamTypes.length) {
      <div class="cl-spacing-box"><button (click)="addDrmTier()" class="btn btn-sm cl-color-secondary" type="button">Add tier</button></div>
    }
    }
          </div>
        </details>
      </div>
    </div>
          </div>
    </fieldset>
  </div>
</div>
